import { format } from 'date-fns';

import { useInfiniteQuery } from '@tanstack/react-query';

import { useProfile } from 'context/profile';
import {
  DayData,
  DayPaginationResponse,
  dischargeCalendarQueryKeys,
  indexDischargeCalendarDay,
} from 'services/api/dischargeCalendar';
import { useDischargeCalendarFilters, useDischargeCalendarSearch } from 'stores/dischargeCalendarStore';

interface UseDischargeCalendarDayInfiniteOptions {
  date: Date;
  initialData?: DayData;
  waitForInitialData?: boolean;
}

export default function useDischargeCalendarDayInfinite({
  date,
  initialData,
  waitForInitialData = false,
}: UseDischargeCalendarDayInfiniteOptions) {
  const { profile } = useProfile();
  const filters = useDischargeCalendarFilters();
  const search = useDischargeCalendarSearch();
  const dateString = format(date, 'yyyy-MM-dd');

  const hasInitialData = initialData !== undefined;

  const hasMorePages = initialData ? initialData.meta.totalPages > 1 : false;

  const enabled = waitForInitialData ? hasInitialData && hasMorePages : true;

  return useInfiniteQuery<DayPaginationResponse>({
    queryKey: dischargeCalendarQueryKeys.day(dateString, {
      clientId: profile.actingClientId,
      filters,
      search,
    }),
    queryFn: ({ pageParam, signal }) =>
      indexDischargeCalendarDay(
        {
          date,
          page: pageParam as number,
          filters,
          search,
        },
        signal
      ),
    initialPageParam: hasInitialData ? 2 : 1,
    getNextPageParam: (lastPage, _pages, lastPageParam) => {
      return lastPage.meta.totalPages > (lastPageParam as number) ? (lastPageParam as number) + 1 : undefined;
    },
    initialData: initialData
      ? {
          pages: [{ data: initialData.data, meta: initialData.meta }],
          pageParams: [1],
        }
      : undefined,
    // Prevent automatic refetch - only fetch when user scrolls to load more
    staleTime: Infinity,
    enabled,
  });
}
